import {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useRef,
} from "react";

import axios from "axios";

import API_URL from "../config/api";
import apiService from "../services/api";
import heartbeatService from "../services/heartbeat";
import { setCookie, deleteCookie } from "../utils/cookies";
import {
  beginAuthTransition,
  clearStoredImpersonation,
  decodeDashboardToken,
  endAuthTransition,
  getCurrentLocationPath,
  getStoredAdminReturnPath,
  getStoredAdminToken,
  isAdminDashboardToken,
  isImpersonationToken,
  sanitizeAdminReturnPath,
  shouldDeferUnauthorizedRecovery,
  storeAdminReturnPath,
} from "../utils/impersonation";
import { recoverStoredAdminSession } from "../utils/impersonationRecovery";
import { replaceLocation } from "../utils/navigation";

const AuthContext = createContext();

const decodeToken = decodeDashboardToken;

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonatingUser, setImpersonatingUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("authToken"));
  const [adminToken, setAdminToken] = useState(
    localStorage.getItem("adminToken")
  );
  const [unclaimedRedeemCodes, setUnclaimedRedeemCodes] = useState([]);
  const [totalRedeemCodes, setTotalRedeemCodes] = useState(0);
  const authTransitionRef = useRef(false);
  const authBootstrapGenerationRef = useRef(0);

  const clearRoleScopedStorage = useCallback(() => {
    localStorage.removeItem("lastActivityTime");
    localStorage.removeItem("dashboardLayouts");
    localStorage.removeItem("viewedTickets");
    localStorage.removeItem("searchHistory");
    localStorage.removeItem("hiddenWidgets");
    localStorage.removeItem("adminHistory");
  }, []);

  const clearAuthState = useCallback(
    ({ clearAuthToken = true, stopHeartbeat = true } = {}) => {
      authTransitionRef.current = false;
      endAuthTransition();

      if (stopHeartbeat) {
        heartbeatService.stop();
      }

      clearStoredImpersonation();
      clearRoleScopedStorage();

      if (clearAuthToken) {
        localStorage.removeItem("authToken");
        deleteCookie("authToken");
        delete axios.defaults.headers.common["Authorization"];
        setToken(null);
      }

      setAdminToken(null);
      setImpersonatingUser(null);
      setUser(null);
      setUnclaimedRedeemCodes([]);
      setTotalRedeemCodes(0);
    },
    [clearRoleScopedStorage]
  );

  const fetchUserProfile = useCallback(
    async (authToken = token) => {
      const response = await apiService.getDashboardProfile();

      if (response.status === 200) {
        const dashboardData = response.data;
        const decodedToken = decodeToken(authToken);
        const role = decodedToken?.role || "user";

        // Extract user ID from JWT token
        // For admins, use adminId; for users, use registrationId or other fields
        const userId =
          decodedToken?.adminId || // Admin users
          decodedToken?.registrationId || // Regular users
          decodedToken?.sub ||
          decodedToken?.id ||
          decodedToken?.userId ||
          decodedToken?.user_id ||
          decodedToken?.email; // Fallback to email as unique identifier

        const userWithSettings = {
          id: userId,
          email: decodedToken?.email || "",
          role,
          name:
            `${dashboardData.firstName || ""} ${dashboardData.lastName || ""}`.trim() ||
            dashboardData.name ||
            dashboardData.email?.split("@")[0] ||
            "User",
          firstName: dashboardData.firstName || "",
          lastName: dashboardData.lastName || "",
          avatarUrl: dashboardData.avatarUrl || null,
          phone: dashboardData.phone || "",
          company: dashboardData.company || "",
          position: dashboardData.position || "",
          location: dashboardData.location || "",
          website: dashboardData.website || "",
          bio: dashboardData.bio || "",
          timezone: dashboardData.timezone || "UTC",
          joinDate: dashboardData.joinDate || new Date().toISOString(),
          type: dashboardData.type || "Free User",
          resellerManaged: dashboardData.resellerManaged === true,
          resellerTier: dashboardData.resellerTier || null,
          resellerTierOrder: dashboardData.resellerTierOrder || null,
          resellerStatus: dashboardData.resellerStatus || null,
          resellerType: dashboardData.resellerType || null,
          preferences: dashboardData.preferences || {
            showWelcomeBanner: true,
          },
        };

        setUser(userWithSettings);
      }
    },
    [token]
  );

  const login = async (email, password) => {
    try {
      const response = await apiService.login(email, password);

      if (response.status === 200 && response.auth === true) {
        const { token: authToken } = response;

        clearAuthState({ clearAuthToken: false, stopHeartbeat: false });
        setToken(authToken);
        localStorage.setItem("authToken", authToken);
        localStorage.setItem("loginTime", Date.now().toString()); // Track login time

        // Store authToken in a cookie that expires in 30 days
        setCookie("authToken", authToken, 30);

        axios.defaults.headers.common["Authorization"] = `Bearer ${authToken}`;

        // Update login status in backend
        apiService.updateUserLoginStatus("online").catch(console.error);

        return { success: true, firstName: response.data.firstName };
      } else {
        return {
          success: false,
          error: response.data.error || "Invalid credentials",
        };
      }
    } catch (error) {
      if (error.message) {
        return {
          success: false,
          error: error.message,
        };
      }

      return {
        success: false,
        error: "Login failed. Please try again.",
      };
    }
  };

  const register = async (registrationData) => {
    try {
      const response = await apiService.register(registrationData);

      if (response.status === 201) {
        const { data: responseData } = response;

        return {
          success: true,
          email: responseData.email,
          registrationId: responseData.registrationId,
          subscriptionPlan: responseData.subscriptionPlan,
        };
      } else {
        return {
          success: false,
          error: response.error || response.message || "Registration failed",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Registration failed",
      };
    }
  };

  const logout = () => {
    // Stop heartbeat service on logout
    heartbeatService.stop();

    // Track logout time and update status
    localStorage.setItem("logoutTime", Date.now().toString());
    localStorage.removeItem("loginTime");

    // Update logout status in backend
    apiService.updateUserLoginStatus("offline").catch(console.error);

    clearAuthState({ stopHeartbeat: false });
  };

  const updateProfile = async (updatedData) => {
    try {
      // Convert name to firstName and lastName if provided
      const dataToSend = { ...updatedData };

      if (updatedData.name && !updatedData.firstName && !updatedData.lastName) {
        const nameParts = updatedData.name.trim().split(" ");

        dataToSend.firstName = nameParts[0] || "";
        dataToSend.lastName = nameParts.slice(1).join(" ") || "";
        delete dataToSend.name;
      }

      const response = await axios.patch(
        `${API_URL}/v1/dashboard/profile`,
        dataToSend
      );

      if (response.data.status === 200) {
        const updatedInfo = response.data.data;

        // Update user object with response data
        const updatedUser = {
          ...user,
          ...updatedInfo,
          avatarUrl: updatedInfo.avatarUrl || user.avatarUrl,
          name: `${updatedInfo.firstName || ""} ${updatedInfo.lastName || ""}`.trim(),
          preferences: updatedInfo.preferences ||
            user.preferences || {
              showWelcomeBanner: true,
            },
        };

        setUser(updatedUser);

        return { success: true };
      } else {
        throw new Error(response.data.error || "Failed to update profile");
      }
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          "Failed to update profile",
      };
    }
  };

  const updateAvatar = async (avatarData) => {
    try {
      if (avatarData === null) {
        const response = await apiService.deleteAvatar();

        if (response.status === 200) {
          await fetchUserProfile();

          return { success: true };
        } else {
          throw new Error(response.error || "Failed to remove avatar");
        }
      } else {
        await fetchUserProfile();

        return { success: true };
      }
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.error ||
          error.message ||
          "Failed to update avatar",
      };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await apiService.changePassword(
        currentPassword,
        newPassword
      );

      if (response.status === 200) {
        return { success: true };
      } else {
        throw new Error(response.error || "Failed to change password");
      }
    } catch (error) {
      if (
        error.message?.includes("400") ||
        error.message?.includes("incorrect")
      ) {
        return {
          success: false,
          error: "Current password is incorrect",
        };
      }

      return {
        success: false,
        error: error.message || "Failed to change password",
      };
    }
  };

  const startImpersonation = async (customerId, options = {}) => {
    if (authTransitionRef.current) {
      return {
        success: false,
        error: "An authentication change is already in progress",
      };
    }

    const currentAdminToken = token || localStorage.getItem("authToken");

    if (!isAdminDashboardToken(currentAdminToken)) {
      return {
        success: false,
        error: "Admin session expired. Please log in again.",
      };
    }

    if (adminToken || localStorage.getItem("adminToken")) {
      clearStoredImpersonation();
      setAdminToken(null);
      setImpersonatingUser(null);
    }

    const returnPath = sanitizeAdminReturnPath(
      options.returnTo || getCurrentLocationPath()
    );

    try {
      authTransitionRef.current = true;
      beginAuthTransition();

      const response = await apiService.impersonateCustomer(customerId);

      if (response.status === 200 && response.auth === true) {
        heartbeatService.stop();

        storeAdminReturnPath(returnPath);
        localStorage.setItem("adminToken", currentAdminToken);

        const { token: impersonationToken } = response;

        localStorage.setItem("authToken", impersonationToken);
        setCookie("authToken", impersonationToken, 30);

        // Persist tokens and reload immediately. Avoid React state updates here so
        // in-flight admin-page requests cannot 401 and tear down the session on mobile.
        replaceLocation("/dashboard");

        return { success: true };
      }

      authTransitionRef.current = false;
      endAuthTransition();

      return {
        success: false,
        error: response.error || "Failed to impersonate user",
      };
    } catch (error) {
      authTransitionRef.current = false;
      endAuthTransition();

      return {
        success: false,
        error: error.message || "Failed to impersonate user",
      };
    }
  };

  const stopImpersonation = async () => {
    if (authTransitionRef.current) {
      return {
        success: false,
        error: "An authentication change is already in progress",
      };
    }

    const currentAdminToken = adminToken || localStorage.getItem("adminToken");
    const returnPath = getStoredAdminReturnPath({
      customerId: impersonatingUser?.id,
    });

    // If the parked admin JWT is missing/expired, end the impersonation session
    // and send the admin to login so they are never stuck unable to re-authenticate.
    if (!currentAdminToken || !isAdminDashboardToken(currentAdminToken)) {
      clearAuthState();
      replaceLocation(`/login?redirect=${encodeURIComponent(returnPath)}`);

      return {
        success: false,
        error: "Admin session expired. Please log in again.",
      };
    }

    authTransitionRef.current = true;
    beginAuthTransition();

    heartbeatService.stop();

    localStorage.setItem("authToken", currentAdminToken);
    setCookie("authToken", currentAdminToken, 30);

    replaceLocation(returnPath);

    return { success: true };
  };

  const fetchRedeemCodes = useCallback(async () => {
    try {
      const response = await apiService.getUnclaimedRedeemCodes();

      if (response?.data) {
        const allCodes = response.data || [];
        const unclaimedCodesFiltered = allCodes.filter(
          (code) => code.status === "UNCLAIMED" && code.active === true
        );

        setUnclaimedRedeemCodes(unclaimedCodesFiltered);
        setTotalRedeemCodes(unclaimedCodesFiltered.length);

        return unclaimedCodesFiltered;
      }

      setUnclaimedRedeemCodes([]);
      setTotalRedeemCodes(0);

      return [];
    } catch (error) {
      setUnclaimedRedeemCodes([]);
      setTotalRedeemCodes(0);

      return [];
    }
  }, []);

  useEffect(
    () => () => {
      heartbeatService.stop();
    },
    []
  );

  useEffect(() => {
    const bootstrapGeneration = ++authBootstrapGenerationRef.current;
    const isStaleBootstrap = () =>
      bootstrapGeneration !== authBootstrapGenerationRef.current;

    const initializeAuth = async () => {
      try {
        if (token) {
          axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

          await fetchUserProfile();
          if (isStaleBootstrap()) return;

          heartbeatService.start();

          const decodedToken = decodeToken(token);

          if (adminToken) {
            if (decodedToken?.role === "admin") {
              clearStoredImpersonation();
              setAdminToken(null);
              setImpersonatingUser(null);
              endAuthTransition();
              authTransitionRef.current = false;
            } else if (!isAdminDashboardToken(adminToken)) {
              // Parked admin JWT expired/invalid, but the current session is still
              // live (usually impersonation). Drop only the stale park — never wipe
              // the live session or a newer login that replaced localStorage mid-flight.
              // Keep impersonationReturnPath so "Return to Admin" can still land on login
              // with a useful redirect after the admin re-authenticates.
              localStorage.removeItem("adminToken");
              setAdminToken(null);

              if (decodedToken?.isImpersonation === true) {
                setImpersonatingUser({
                  id: decodedToken?.registrationId,
                  email: decodedToken?.email || "User",
                });
              } else {
                setImpersonatingUser(null);
              }
            } else {
              setImpersonatingUser({
                id: decodedToken?.registrationId,
                email: decodedToken?.email || "User",
              });
            }
          } else if (decodedToken?.isImpersonation === true) {
            setImpersonatingUser({
              id: decodedToken?.registrationId,
              email: decodedToken?.email || "User",
            });
          }

          if (decodedToken?.role === "user" || !decodedToken?.role) {
            await fetchRedeemCodes();
            if (isStaleBootstrap()) return;
          }

          endAuthTransition();
          authTransitionRef.current = false;
        } else {
          heartbeatService.stop();
          setUser(null);
          setUnclaimedRedeemCodes([]);
          setTotalRedeemCodes(0);
        }
      } catch (error) {
        if (isStaleBootstrap()) return;

        // A newer login may have replaced the token while this bootstrap was in flight.
        const storedToken = localStorage.getItem("authToken");

        if (storedToken && storedToken !== token) {
          if (isAdminDashboardToken(storedToken)) {
            setToken(storedToken);
            setAdminToken(null);
            setImpersonatingUser(null);
            endAuthTransition();
            authTransitionRef.current = false;
          }

          return;
        }

        if (shouldDeferUnauthorizedRecovery()) {
          if (storedToken) {
            try {
              axios.defaults.headers.common["Authorization"] =
                `Bearer ${storedToken}`;
              await fetchUserProfile(storedToken);
              if (isStaleBootstrap()) return;

              heartbeatService.start();

              const decodedToken = decodeToken(storedToken);

              if (getStoredAdminToken()) {
                setImpersonatingUser({
                  id: decodedToken?.registrationId,
                  email: decodedToken?.email || "User",
                });
              }

              if (decodedToken?.role === "user" || !decodedToken?.role) {
                await fetchRedeemCodes();
                if (isStaleBootstrap()) return;
              }

              endAuthTransition();
              authTransitionRef.current = false;

              return;
            } catch (retryError) {
              // Keep the impersonation session intact; do not auto-restore admin.
              return;
            }
          }

          return;
        }

        if (storedToken !== token && isAdminDashboardToken(storedToken)) {
          setToken(storedToken);
          setAdminToken(null);
          setImpersonatingUser(null);
          endAuthTransition();
          authTransitionRef.current = false;

          return;
        }

        const restored = await recoverStoredAdminSession({
          validateToken: (adminTokenToValidate) =>
            apiService.validateToken(adminTokenToValidate),
        }).catch(() => null);

        if (isStaleBootstrap()) return;

        if (restored) {
          setToken(restored.adminToken);
          setAdminToken(null);
          setImpersonatingUser(null);
          endAuthTransition();
          authTransitionRef.current = false;

          return;
        }

        // Final guard: never clear a token that appeared after this bootstrap started.
        if (localStorage.getItem("authToken") !== token) {
          return;
        }

        clearAuthState();
      } finally {
        if (!isStaleBootstrap()) {
          setLoading(false);
        }
      }
    };

    initializeAuth();
  }, [token, adminToken, clearAuthState, fetchRedeemCodes, fetchUserProfile]);

  const parkedAdminToken = adminToken || getStoredAdminToken();
  // Allow stop whenever impersonating so expired parked admin can escape to login.
  const canStopImpersonation =
    !!parkedAdminToken || isImpersonationToken(token);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        logout,
        register,
        updateAvatar,
        updateProfile,
        changePassword,
        fetchUserProfile,
        isAuthenticated: !!user,
        isImpersonating: !!adminToken || isImpersonationToken(token),
        canStopImpersonation,
        impersonatingUser,
        startImpersonation,
        stopImpersonation,
        unclaimedRedeemCodes,
        totalRedeemCodes,
        fetchRedeemCodes,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
